// SaveChartDialog.jsx
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  IconButton,
  Box,
  Collapse,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  FormControlLabel,
  Switch,
  RadioGroup,
  Radio,
  FormControl,
} from "@mui/material";
import {
  CreateNewFolder,
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Delete as DeleteIcon,
  ExpandLess,
  ExpandMore,
} from "@mui/icons-material";
import Label from "../../../components/Label/Label";
import { initialFolders } from "./constants";
import { Star, StarBorder } from "@mui/icons-material";
/**
 * Helpers
 */
const attachParentIds = (nodes = [], parentId = null) =>
  (nodes || []).map((n) => ({
    ...n,
    parentId,
    children: attachParentIds(n.children || [], n.id),
  }));

const findSiblings = (nodes = [], parentId) => {
  if (parentId == null) return nodes;

  for (const n of nodes) {
    if (n.id === parentId) return n.children || [];
    const found = findSiblings(n.children || [], parentId);
    if (found !== null) return found;
  }
  return null;
};

const findParentId = (nodes = [], id, parentId = null) => {
  for (const n of nodes) {
    if (n.id === id) return parentId;
    const found = findParentId(n.children || [], id, n.id);
    if (found !== null && found !== undefined) return found;
  }
  return null;
};

const normalize = (s) => (s || "").toString().toLowerCase().trim();
const isNameUnique = (siblings = [], name, ignoreId = null) => {
  return !siblings.some((s) => normalize(s.name) === normalize(name));
};

/**
 * FolderItem (recursive)
 */
const FolderItem = ({
  folder,
  onSelect,
  selectedId,
  onRequestNewSubFolder,
  onRename,
  onDelete,
}) => {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [tempName, setTempName] = useState(folder.name);

  const handleRename = () => {
    if (tempName.trim()) {
      const renamed = onRename(folder.id, tempName.trim(), folder.parentId);
      if (renamed) setEditing(false);
    }
  };

  useEffect(() => {
    setTempName(folder.name);
  }, [folder.name]);

  return (
    <>
      <ListItem
        dense
        selected={selectedId === folder.id}
        sx={{
          borderRadius: 1,
          "&.Mui-selected": { bgcolor: "action.selected" },
          "&:hover": { bgcolor: "action.hover" },
          pl: 1,
        }}
        onClick={() => onSelect(folder)}
        secondaryAction={
          <Box display="flex" alignItems="center" gap={0.5}>
            {editing ? (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRename();
                }}
              >
                <CheckIcon fontSize="small" />
              </IconButton>
            ) : (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setEditing(true);
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            )}

            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onRequestNewSubFolder(folder.id);
                setOpen(true);
              }}
            >
              <CreateNewFolder fontSize="small" />
            </IconButton>

            <IconButton
              size="small"
              disabled={!folder.isNew}
              sx={{ "&.Mui-disabled": { opacity: 0.3 } }}
              onClick={(e) => {
                e.stopPropagation();
                if (folder.isNew) onDelete(folder.id);
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>

            <IconButton
              size="small"
              disabled={!folder.children || folder.children.length === 0}
              sx={{ "&.Mui-disabled": { opacity: 0.3 } }}
              onClick={(e) => {
                e.stopPropagation();
                if (folder.children?.length > 0) setOpen(!open);
              }}
            >
              {open ? (
                <ExpandLess fontSize="small" />
              ) : (
                <ExpandMore fontSize="small" />
              )}
            </IconButton>
          </Box>
        }
      >
        <ListItemIcon sx={{ minWidth: 28 }}>
          {open ? (
            <FolderOpenIcon fontSize="small" />
          ) : (
            <FolderIcon fontSize="small" />
          )}
        </ListItemIcon>

        {editing ? (
          <TextField
            value={tempName}
            onChange={(e) => setTempName(e.target.value)}
            size="small"
            autoFocus
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleRename();
              if (e.key === "Escape") setEditing(false);
            }}
            sx={{ maxWidth: 160 }}
          />
        ) : (
          <ListItemText
            primary={
              // <Typography fontSize="14px" fontWeight={500}>
              <Label text={folder.name} fontSize="14px" fontWeight={500} />
              // </Typography>
            }
          />
        )}
      </ListItem>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <List component="div" disablePadding sx={{ pl: 3 }}>
          {(folder.children || []).map((child) => (
            <FolderItem
              key={child.id}
              folder={child}
              onSelect={onSelect}
              selectedId={selectedId}
              onRequestNewSubFolder={onRequestNewSubFolder}
              onRename={onRename}
              onDelete={onDelete}
            />
          ))}
        </List>
      </Collapse>
    </>
  );
};

/**
 * NewFolderDialog
 */
const NewFolderDialog = ({ open, onClose, onCreate, siblings }) => {
  const [name, setName] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = () => {
    if (!name.trim()) {
      setError("Folder name cannot be empty");
      return;
    }
    if (!isNameUnique(siblings, name)) {
      setError("A folder with this name already exists here");
      return;
    }
    onCreate(name.trim());
    setName("");
    setError("");
    onClose();
  };

  useEffect(() => {
    if (open) {
      setName("");
      setError("");
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>
        <Label text="New Folder" />
      </DialogTitle>
      <DialogContent>
        <TextField
          size="small"
          autoFocus
          fullWidth
          label="Folder Name"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (!isNameUnique(siblings, e.target.value))
              setError("A folder with this name already exists here");
            else setError("");
          }}
          error={!!error}
          helperText={error}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSubmit();
          }}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!!error}>
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * SaveChartDialog
 */
const SaveChartDialog = ({
  open,
  onClose,
  onSave,
  // , chartState, chartData
  typeOfMode,
  version_id,
}) => {
  const initialState = attachParentIds(initialFolders || []);
  const [folders, setFolders] = useState(initialState);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [vizInputs, setViztInputs] = useState({
    name: "",
    description: "",
    dataEmbedded: true,
    isFavourite: false,
  });

  const [newFolderParent, setNewFolderParent] = useState(null);
  // const [showNewFolderDialog, setShowNewFolderDialog] = useState(false);
  const [error, setError] = useState("");
  const [descError, setDescError] = useState("");

  const MAX_NAME_LENGTH = 30;
  const MAX_DESC_LENGTH = 200;

  const resetState = () => {
    setFolders(initialState);
    setSelectedFolder(null);
    // setChartName("");
    setNewFolderParent(null);
    // setShowNewFolderDialog(false);
    setViztInputs({
      name: "",
      description: "",
      dataEmbedded: true,
      isFavourite: false, // reset
    });
  };

  // const handleRequestNewFolder = (parentId = null) => {
  //   setNewFolderParent(parentId);
  //   setShowNewFolderDialog(true);
  // };

  const handleCreateFolder = (name) => {
    const newFolder = { id: Date.now(), name, children: [], isNew: true };
    if (newFolderParent === null) setFolders((prev) => [...prev, newFolder]);
    else {
      const addChild = (nodes) =>
        nodes.map((n) =>
          n.id === newFolderParent
            ? { ...n, children: [...(n.children || []), newFolder] }
            : { ...n, children: addChild(n.children || []) },
        );
      setFolders((prev) => addChild(prev));
    }
    setSelectedFolder(newFolder);
  };

  const handleRename = (id, newName, parentId) => {
    if (!newName.trim()) return false;
    const pid = parentId ?? findParentId(folders, id) ?? null;
    const siblings = findSiblings(folders, pid) || [];
    if (!isNameUnique(siblings, newName, id)) return false;

    const rename = (nodes = []) =>
      nodes.map((n) =>
        n.id === id
          ? { ...n, name: newName }
          : { ...n, children: rename(n.children || []) },
      );

    setFolders((prev) => rename(prev));
    setSelectedFolder((prevSel) =>
      prevSel && prevSel.id === id ? { ...prevSel, name: newName } : prevSel,
    );
    return true;
  };

  const handleDelete = (id) => {
    const remove = (nodes = []) =>
      nodes
        .filter((n) => n.id !== id)
        .map((n) => ({ ...n, children: remove(n.children || []) }));
    setFolders((prev) => remove(prev));
    if (selectedFolder?.id === id) setSelectedFolder(null);
  };

  const handleSave = () => {
    if (!vizInputs.name.trim()) {
      setError("Chart name is required");
      return;
    }
    onSave({
      ...vizInputs,
      // chartState,
      // chartData,
    });
    resetState();
    onClose();
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleNameChange = (e) => {
    let value = e.target.value.trimStart();

    if (value.length > MAX_NAME_LENGTH) {
      setError(`Maximum ${MAX_NAME_LENGTH} characters allowed`);
      return;
    }

    setError("");
    setViztInputs((prev) => ({ ...prev, name: value }));
  };
  const handleDescriptionChange = (e) => {
    let value = e.target.value.trimStart();

    if (value.length > MAX_DESC_LENGTH) {
      setDescError(`Maximum ${MAX_DESC_LENGTH} characters allowed`);
      return;
    }

    setDescError("");
    setViztInputs((prev) => ({ ...prev, description: value }));
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>
          <Label text="Save Chart" fontSize="16px" fontWeight="500" />
        </DialogTitle>
        <DialogContent
          dividers
          sx={{ display: "flex", flexDirection: "column", p: 1.5 }}
        >
          {/* <Box // dont remove this code as we need it for phase 2
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
          >
            <Label text="Folders" fontSize="14px" />
            <Button
              size="small"
              variant="outlined"
              onClick={() => handleRequestNewFolder(null)}
              startIcon={<CreateNewFolder />}
            >
              <Label text="New Folder" />
            </Button>
          </Box>
          <Divider /> */}

          {/* <Box flex={1} overflow="auto" mt={1}> // dont remove this code as we need it for phase 2
            <List dense disablePadding>
              {(folders || []).map((folder) => (
                <FolderItem
                  key={folder.id}
                  folder={folder}
                  onSelect={setSelectedFolder}
                  selectedId={selectedFolder?.id}
                  onRequestNewSubFolder={handleRequestNewFolder}
                  onRename={handleRename}
                  onDelete={handleDelete}
                />
              ))}
            </List>
          </Box> */}
          {/* <Divider sx={{ my: 1 }} /> */}

          <TextField
            label="Chart Name"
            fullWidth
            margin="normal"
            value={vizInputs.name}
            onChange={handleNameChange}
            error={Boolean(error)}
            helperText={
              error || `${vizInputs.name.length}/${MAX_NAME_LENGTH} characters`
            }
            FormHelperTextProps={{
              sx: { color: error ? "error.main" : "text.secondary" },
            }}
          />

          <TextField
            label="Description"
            fullWidth
            margin="normal"
            multiline
            rows={4}
            value={vizInputs.description}
            onChange={handleDescriptionChange}
            error={Boolean(descError)}
            helperText={
              descError ||
              `${vizInputs.description.length}/${MAX_DESC_LENGTH} characters`
            }
            FormHelperTextProps={{
              sx: { color: descError ? "error.main" : "text.secondary" },
            }}
          />
          <FormControl>
            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
              Save Mode
            </Typography>

            <RadioGroup
              value={vizInputs.dataEmbedded ? "graph" : "template"}
              onChange={(e) => {
                const isGraph = e.target.value === "graph";

                setViztInputs((prev) => ({
                  ...prev,
                  dataEmbedded: isGraph, // true → graph, false → template
                }));
              }}
              style={{ display: "flex", flexDirection: "row", gap: "8px" }}
            >
              {/* Save as Graph */}
              <FormControlLabel
                value="graph"
                control={<Radio />}
                label="Save as Graph"
              />

              {/* Save as Template */}
              <FormControlLabel
                value="template"
                control={<Radio />}
                label="Save as Template"
              />
            </RadioGroup>
          </FormControl>

          {(typeOfMode === "create" || version_id === 0) && (
            <Box mt={1} display="flex" alignItems="center" gap={1}>
              <IconButton
                onClick={() =>
                  setViztInputs((prev) => ({
                    ...prev,
                    isFavourite: !prev.isFavourite,
                  }))
                }
              >
                {vizInputs.isFavourite ? (
                  <Star sx={{ color: "#f5b301" }} />
                ) : (
                  <StarBorder />
                )}
              </IconButton>

              <Typography fontSize="13px" fontWeight={500}>
                Mark as Favourite
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => handleClose()} variant="outlined">
            <Label text="Cancel" color="inherit" />
          </Button>
          <Button
            onClick={handleSave}
            color="primary"
            variant="contained"
            disabled={!vizInputs.name.trim() || error || descError}
          >
            <Label text="Submit" color="inherit" />
          </Button>
        </DialogActions>
      </Dialog>

      {/* <NewFolderDialog
        open={showNewFolderDialog}
        onClose={() => setShowNewFolderDialog(false)}
        onCreate={handleCreateFolder}
        siblings={findSiblings(folders, newFolderParent)}
      /> */}
    </>
  );
};

export default SaveChartDialog;
